// CountUp — плавная анимация числа от прежнего значения к новому (rAF,
// easeOutCubic). «Оживляет» KPI/статистику: число не прыгает скачком, а
// набегает за ~0.6с. Под prefers-reduced-motion — мгновенно финальное значение.
//
// Использование:
//   <CountUp value={streakDays} />                       // целое
//   <CountUp value={accuracy} format={(n) => `${Math.round(n)}%`} />
//   const shown = useCountUp(value);                      // хук для своей вёрстки

import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

const DEFAULT_DURATION = 600;

function easeOutCubic(t: number): number {
	return 1 - (1 - t) ** 3;
}

export function useCountUp(value: number, durationMs = DEFAULT_DURATION): number {
	const reduced = usePrefersReducedMotion();
	const [display, setDisplay] = useState(value);
	const fromRef = useRef(value);
	const rafRef = useRef<number | null>(null);
	const startRef = useRef<number | null>(null);

	useEffect(() => {
		if (reduced || durationMs <= 0) {
			setDisplay(value);
			return;
		}
		const from = fromRef.current;
		const to = value;
		if (from === to) return;
		startRef.current = null;

		const tick = (now: number): void => {
			if (startRef.current === null) startRef.current = now;
			const elapsed = now - startRef.current;
			const p = Math.min(1, elapsed / durationMs);
			const eased = easeOutCubic(p);
			setDisplay(from + (to - from) * eased);
			if (p < 1) {
				rafRef.current = requestAnimationFrame(tick);
			} else {
				fromRef.current = to;
			}
		};
		rafRef.current = requestAnimationFrame(tick);
		return () => {
			if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
			fromRef.current = value;
		};
	}, [value, durationMs, reduced]);

	return display;
}

interface CountUpProps {
	value: number;
	/** Формат отображения. По умолчанию — округлённое целое. */
	format?: (n: number) => string;
	durationMs?: number;
	className?: string;
}

export function CountUp({
	value,
	format,
	durationMs,
	className,
}: CountUpProps): React.ReactElement {
	const shown = useCountUp(value, durationMs);
	const text = format ? format(shown) : String(Math.round(shown));
	return (
		<span className={className} style={{ fontVariantNumeric: "tabular-nums" }}>
			{text}
		</span>
	);
}
